import { DG_STORE_CONFIG, buildWhatsAppUrl } from '@dg-embalajes/commerce';
import { PLOT_MATERIALS } from '@dg-embalajes/product-domain';
import { buildPdfFileName } from '@dg-embalajes/pdf-engine';
import {
  AlertTriangle,
  CheckCircle2,
  Cloud,
  Download,
  ExternalLink,
  LoaderCircle,
  MessageCircle,
  Share2,
  Smartphone,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { COPY, honestWhatsAppMessage } from '@/content/copy';
import type { AccountSnapshot } from '@/ports/account';
import type { EditorPort, EditorSnapshot } from '@/ports/editor';
import type { CloudSaveResult } from '@/ports/projects';
import { createPrintablePdfScene } from '@/runtime/pdfScene';

function buildFilename(snapshot: EditorSnapshot): string {
  return snapshot.document
    ? buildPdfFileName(createPrintablePdfScene(snapshot.document))
    : 'diseno-dg-embalajes.pdf';
}

interface FinishPanelProps {
  readonly editor: EditorPort;
  readonly snapshot: EditorSnapshot;
  readonly account: AccountSnapshot;
  readonly fontsReady: boolean;
  readonly onSaveCloud: () => void;
  readonly onOfferPwa: () => void;
  readonly cloudSaveResult: CloudSaveResult | null;
}

export function FinishPanel({ editor, snapshot, account, fontsReady, onSaveCloud, onOfferPwa, cloudSaveResult }: FinishPanelProps) {
  const [actionError, setActionError] = useState<string | null>(null);
  const product = snapshot.document?.product;
  const filename = useMemo(() => buildFilename(snapshot), [snapshot]);
  if (!product) return null;

  const isTube = product.kind === 'tube';
  const material = isTube ? PLOT_MATERIALS.find((item) => item.id === product.materialId) : null;
  const canExport = snapshot.qualityReady && fontsReady && snapshot.exportStatus !== 'preparing';
  const hasQualityWarnings = snapshot.assetQuality.some((item) => item.status === 'warning');
  const qualityState = !canExport ? 'blocked' : hasQualityWarnings ? 'warning' : 'ready';
  const qualityTitle = qualityState === 'warning'
    ? 'Revisá la calidad antes de generar'
    : qualityState === 'ready'
      ? 'Calidad lista para generar'
      : 'Esperando recursos reales';
  const run = async (action: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await action();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'No se pudo completar la acción.');
    }
  };
  const whatsappUrl = buildWhatsAppUrl(honestWhatsAppMessage(product.kind));

  return (
    <div className="panel-stack finish-panel" data-testid="finish-panel">
      <div className="finish-ready">
        <CheckCircle2 aria-hidden="true" />
        <div>
          <strong>{isTube ? COPY.downloadDesign : COPY.downloadSticker}</strong>
          <span>{isTube ? 'Tu diseño está listo.' : 'Tu sticker está listo.'} Revisá medidas, calidad y nombre antes de generar el archivo.</span>
        </div>
      </div>

      <div className="finish-preview" aria-label="Vista previa limpia">
        {snapshot.cleanPreviewUrl ? <img src={snapshot.cleanPreviewUrl} alt="Vista previa limpia del diseño completo" /> : null}
        {snapshot.cleanPreviewStatus === 'preparing' ? <span className="preview-status"><LoaderCircle className="spin" /> Preparando composición completa...</span> : null}
        {snapshot.cleanPreviewStatus === 'error' ? <span className="preview-status is-error" role="alert">{snapshot.cleanPreviewError}</span> : null}
        {snapshot.cleanPreviewStatus === 'idle' && !snapshot.cleanPreviewUrl ? <span>Preparando vista limpia...</span> : null}
      </div>

      <dl className="finish-summary">
        <div><dt>Producto</dt><dd>{isTube ? `Tubo ${product.diameterMm} mm` : product.source.kind === 'preset' ? `${product.source.presetCode} · ${product.source.group}` : 'Sticker personalizado'}</dd></div>
        {isTube ? <div><dt>Material</dt><dd>{material?.label}</dd></div> : <div><dt>Forma</dt><dd>{product.shape === 'circle' ? 'Círculo' : product.shape === 'square' ? 'Cuadrado' : 'Rectángulo'}</dd></div>}
        {isTube ? <div><dt>ALTO</dt><dd>{product.heightMm} mm</dd></div> : <div><dt>ALTO</dt><dd>{product.heightMm} mm</dd></div>}
        <div className="summary-total"><dt>{isTube ? 'DESARROLLO del archivo' : 'PDF real'}</dt><dd>{isTube ? `${product.developmentEffectiveMm} mm` : `${product.widthMm} × ${product.heightMm} mm`}</dd></div>
      </dl>

      <label className="filename-field">Nombre del archivo<input value={filename} readOnly /></label>

      <div className={`quality-status is-${qualityState}`}>
        {qualityState === 'warning'
          ? <AlertTriangle />
          : qualityState === 'ready'
            ? <CheckCircle2 />
            : <LoaderCircle className={snapshot.exportStatus === 'preparing' ? 'spin' : ''} />}
        <span><strong>{qualityTitle}</strong><small>{fontsReady ? snapshot.qualityMessage : 'Las tipografías deben cargar antes de exportar.'}</small></span>
      </div>

      {snapshot.assetQuality.length > 0 ? (
        <ul className="asset-quality-list" aria-label="Calidad de archivos del diseño">
          {snapshot.assetQuality.map((item) => (
            <li key={item.key} className={`is-${item.status}`}>
              <div className="asset-quality-list__header">
                <span className="asset-quality-list__identity">
                  <strong>{item.fileName}</strong>
                  <small>{item.role}{item.pageNumber ? ` · página ${item.pageNumber}` : ''}</small>
                </span>
                <strong className="asset-quality-list__state">{item.status === 'warning' ? 'Revisar' : item.status === 'error' ? 'Error' : 'Listo'}</strong>
              </div>
              <small className="asset-quality-list__message">{item.message}</small>
            </li>
          ))}
        </ul>
      ) : null}

      {actionError || snapshot.exportError ? <p className="inline-error" role="alert">{actionError ?? snapshot.exportError}</p> : null}
      {cloudSaveResult ? <p className={`cloud-save-result is-${cloudSaveResult.kind}`} role="status">{cloudSaveResult.message}</p> : null}

      <div className="finish-actions">
        <button className="primary-action" type="button" disabled={!canExport} onClick={() => void run(() => editor.downloadPdf())}>
          <Download /> {isTube ? 'Descargar PDF final (guardar en tu celular)' : 'Descargar PDF de sticker'}
        </button>
        <button type="button" disabled={!canExport} onClick={() => void run(() => editor.sharePdf())}><Share2 /> Compartir PDF</button>
        <a className="whatsapp-action" href={whatsappUrl} target="_blank" rel="noreferrer"><MessageCircle /> {COPY.whatsappOpen}</a>
        <a href={DG_STORE_CONFIG.url} target="_blank" rel="noreferrer"><ExternalLink /> {COPY.store}</a>
        <button type="button" onClick={onSaveCloud}><Cloud /> {account.status === 'authenticated' ? 'Guardar proyecto en la nube' : 'Guardar en la nube'}</button>
        {snapshot.valueActionCount > 0 ? <button type="button" onClick={onOfferPwa}><Smartphone /> {COPY.pwaAction}</button> : null}
      </div>

      <div className="finish-instructions">
        <p>{COPY.printWarning}</p>
        {isTube ? (
          <>
            <p>Primero podés abrir nuestro WhatsApp para dejar el chat listo. Después descargá el PDF y adjuntalo en esa conversación.</p>
            <ol>
              <li>Abrí el WhatsApp de DG.</li>
              <li>Descargá el PDF final y guardalo.</li>
              <li>Adjuntá ese PDF en el chat para producción.</li>
            </ol>
            <small>{COPY.realPdfNote}</small>
          </>
        ) : (
          <>
            <p>El PDF de stickers sale a medida real con la imagen y una línea fina negra de corte visible para revisión del contorno.</p>
            <ol>
              <li>Descargá o compartí el PDF.</li>
              <li>Enviá ese archivo por WhatsApp.</li>
              <li>DG lo revisa antes de imprimir/cortar.</li>
            </ol>
            <small>Esta herramienta reduce idas y vueltas, pero DG revisa el archivo final antes de producir.</small>
          </>
        )}
      </div>
    </div>
  );
}
